import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import PayPalButton from './PayPalButton';
import { Lock, SquarePen, WandSparkles, Play, RefreshCw, CircleCheck, ArrowLeft, Loader } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

// Define our steps and questions
const STEPS = {
  INTRO: 'intro',
  QUESTIONS: 'questions',
  SUMMARY: 'summary',
  TEASER: 'teaser'
};

const QUESTIONS = [
  {
    id: 'name',
    question: 'Wie soll der Held oder die Heldin deiner Geschichte heißen?',
    type: 'text',
    required: true,
    placeholder: 'z.B. Lina'
  },
  {
    id: 'age',
    question: 'Wie alt ist das Kind, für das wir die Geschichte erstellen?',
    type: 'number',
    required: true,
    placeholder: 'z.B. 3'
  },
  {
    id: 'world',
    question: 'Wo soll die Geschichte spielen?',
    types: ['select', 'text'],
    options: [
      'In der realen Welt',
      'In einer magischen Welt',
      'Im Weltall oder auf anderen Planeten',
      'Unter Wasser oder im Meer'
    ],
    placeholder: 'z.B. Bauernhof',
    required: true
  },
  {
    id: 'interests',
    question: (answers) => `Was mag ${answers.name || 'das Kind'} besonders gerne?`,
    type: 'text',
    required: false,
    placeholder: 'z.B. Pinguine, Pizza, Malen'
  },
  {
    id: 'restrictions',
    question: (answers) => answers.name 
      ? `Was mag ${answers.name} nicht? (Optional)`
      : 'Gibt es etwas, das in der Geschichte nicht vorkommen soll? (Optional)',
    type: 'text',
    required: false,
    placeholder: 'z.B. Monster'
  },
  {
    id: 'message',
    question: 'Soll die Geschichte eine besondere Botschaft vermitteln?',
    type: 'select',
    options: [
      'Freundschaft und Zusammenhalt',
      'Mut und Selbstvertrauen',
      'Kreativität und Fantasie',
      'Hilfsbereitschaft und Mitgefühl',
      'Überrascht mich'
    ],
    required: true
  }
];

const pageVariants = {
  initial: (direction) => ({
    y: direction > 0 ? 20 : -20,
    opacity: 0
  }),
  animate: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.4,
      ease: "easeOut"
    }
  },
  exit: (direction) => ({
    y: direction > 0 ? -20 : 20,
    opacity: 0,
    transition: {
      duration: 0.4,
      ease: "easeIn"
    }
  })
};

const contentVariants = {
  initial: { y: 40, opacity: 0 },
  animate: { 
    y: 0, 
    opacity: 1,
    transition: {
      duration: 0.4,
      ease: "easeOut"
    }
  },
  exit: {
    y: -40,
    opacity: 0,
    transition: {
      duration: 0.4,
      ease: "easeIn"
    }
  }
};

// For staggered children animations
const containerVariants = {
  initial: { },
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  },
  exit: {
    transition: {
      staggerChildren: 0.05,
      staggerDirection: -1
    }
  }
};

// Add specific variants for select options
const optionVariants = {
  initial: { 
    opacity: 0,
    y: 20,
  },
  animate: (index) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      delay: index * 0.1,
      ease: "easeOut"
    }
  }),
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.2,
      ease: "easeIn"
    }
  }
};

// Define voice data once at the top level
const VOICE_DATA = [
  { id: 'voice1', name: 'Stefan', image: '/voices/stefan.png', audioSrc: '/voices/stefan-sample.m4a' },
  { id: 'voice2', name: 'Anna', image: '/voices/anna.png', audioSrc: '/voices/anna-sample.m4a' },
  { id: 'voice3', name: 'Kasper', image: '/voices/kasper.png', audioSrc: '/voices/kasper-sample.m4a' },
  { id: 'voice4', name: 'Karolin', image: '/voices/karolin.png', audioSrc: '/voices/karolin-sample.m4a' },
  { id: 'voice5', name: 'Ben', image: '/voices/ben.png', audioSrc: '/voices/ben-sample.m4a' },
  { id: 'voice6', name: 'Lilly', image: '/voices/lilly.png', audioSrc: '/voices/lilly-sample.m4a' },
];

function Generate() {
  const [currentStep, setCurrentStep] = useState(STEPS.INTRO);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const { user } = useAuth();
  const navigate = useNavigate();
  const formRef = useRef(null);
  const navigationRef = useRef(null);
  const [sessionId] = useState(() => {
    // Try to get existing sessionId from localStorage
    const stored = localStorage.getItem('plottie_session_id');
    if (stored) return stored;
    
    // Create new sessionId if none exists
    const newId = uuidv4();
    localStorage.setItem('plottie_session_id', newId);
    return newId;
  });
  const [teaser, setTeaser] = useState(null);
  const [isLoadingTeaser, setIsLoadingTeaser] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [audio, setAudio] = useState(null);

  // Add new state variables for animation control
  const [hasSelectedVoice, setHasSelectedVoice] = useState(false);
  const [showTeaser, setShowTeaser] = useState(false);
  const [showRefreshButton, setShowRefreshButton] = useState(false);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);

  // Add new state for managing teaser versions
  const [teaserVersions, setTeaserVersions] = useState([]);
  const [currentTeaserIndex, setCurrentTeaserIndex] = useState(0);
  const [isGeneratingNewTeaser, setIsGeneratingNewTeaser] = useState(false);

  // Add effect to manage teaser reveal
  useEffect(() => {
    if (hasSelectedVoice) {
      // Short delay before removing blur
      setTimeout(() => {
        setShowTeaser(true);
      }, 500);
      
      // Show refresh button after teaser is revealed
      setTimeout(() => {
        setShowRefreshButton(true);
      }, 1000);
    }
  }, [hasSelectedVoice]);

  // Add viewport handling
  useEffect(() => {
    const handleResize = () => {
      if (window.visualViewport) {
        const currentHeight = window.innerHeight;
        const viewportHeight = window.visualViewport.height;
        const keyboardHeight = currentHeight - viewportHeight;

        if (formRef.current && navigationRef.current) {
          formRef.current.style.height = `${viewportHeight}px`;

          if ("ontouchstart" in window) {
            navigationRef.current.style.bottom = `${keyboardHeight}px`;
          } else {
            navigationRef.current.style.transform = `translateY(-${keyboardHeight}px)`;
          }
        }
      }
    };

    // Add event listeners
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", handleResize);
      window.visualViewport.addEventListener("scroll", handleResize);
    }
    window.addEventListener("resize", handleResize);

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", handleResize);
        window.visualViewport.removeEventListener("scroll", handleResize);
      }
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Scroll to top on step change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  const handleNext = () => {
    if (currentStep === STEPS.INTRO) {
      setCurrentStep(STEPS.QUESTIONS);
    } else if (currentStep === STEPS.QUESTIONS) {
      if (currentQuestionIndex < QUESTIONS.length - 1) {
        setCurrentQuestionIndex(prev => prev + 1);
      } else {
        setCurrentStep(STEPS.SUMMARY);
      }
    } else if (currentStep === STEPS.SUMMARY) {
      if (teaser) {
        setCurrentStep(STEPS.TEASER);
      } else {
        handleGenerateTeaser();
      }
    }
  };

  // Add keyboard event listener
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'Enter' && currentStep !== STEPS.SUMMARY) {
        // Check if we can proceed and handle the next step
        if (currentStep === STEPS.INTRO || 
            (currentStep === STEPS.QUESTIONS && 
             (!QUESTIONS[currentQuestionIndex].required || answers[QUESTIONS[currentQuestionIndex].id]))
        ) {
          handleNext();
        }
      }
    };

    window.addEventListener('keypress', handleKeyPress);
    return () => window.removeEventListener('keypress', handleKeyPress);
  }, [currentStep, currentQuestionIndex, answers]); // Simplified dependencies

  // Move handleGenerate memoization to the top of the component
  const handleGenerate = useCallback(async (paypalOrderId) => {
    try {
      const generateStoryFromTeaser = httpsCallable(functions, 'generateStoryFromTeaser', {
        timeout: 540000
      });

      const currentTeaser = teaserVersions[currentTeaserIndex];

      generateStoryFromTeaser({
        answers,
        sessionId,
        teaser: currentTeaser.teaser,
        title: currentTeaser.title,
        tags: currentTeaser.tags,
        voiceId: selectedVoice,
        paypalOrderId
      })
        .catch(error => {
          console.error('Error triggering story generation:', error);
        });

      navigate('/meine-geschichten', {
        state: {
          pendingStory: {
            answers,
            startedAt: new Date(),
            sessionId,
            teaser: currentTeaser.teaser,
            title: currentTeaser.title,
            tags: currentTeaser.tags,
            voiceId: selectedVoice
          }
        }
      });
    } catch (error) {
      console.error('Error initiating story generation:', error);
    }
  }, [answers, sessionId, teaserVersions, currentTeaserIndex, selectedVoice, navigate]);

  // Memoize the payment handlers with minimal dependencies
  const handlePaymentSuccess = useCallback(async (order, paypalOrderId) => {
    try {
      await handleGenerate(paypalOrderId);
    } catch (error) {
      console.error('Error generating story after payment:', error);
    }
  }, [handleGenerate]);

  const handlePaymentError = useCallback((error) => {
    console.error('Payment failed:', error);
  }, []);

  // Create a wrapper component for PayPal button to ensure stable rendering
  const PayPalButtonWrapper = useMemo(() => {
    return (
      <div className="flex-grow">
        <PayPalButton 
          onSuccess={handlePaymentSuccess}
          onError={handlePaymentError}
        />
      </div>
    );
  }, [handlePaymentSuccess, handlePaymentError]);

  const handleGenerateTeaser = async () => {
    setIsLoadingTeaser(true);
    try {
      const generateTeaser = httpsCallable(functions, 'generateTeaser');
      const result = await generateTeaser({ answers, sessionId });
      setTeaserVersions([result.data]); // Store first version in array
      setTeaser(result.data);
      setCurrentTeaserIndex(0);
      setCurrentStep(STEPS.TEASER);
    } catch (error) {
      console.error('Error generating teaser:', error);
    } finally {
      setIsLoadingTeaser(false);
    }
  };

  const handleGenerateAlternativeTeaser = async () => {
    setIsGeneratingNewTeaser(true);
    try {
      const generateTeaser = httpsCallable(functions, 'generateTeaser');
      const result = await generateTeaser({ answers, sessionId });
      
      // Add new version and update current teaser with all properties
      setTeaserVersions(prev => [...prev, result.data]);
      setTeaser({
        teaser: result.data.teaser,
        title: result.data.title,
        tags: result.data.tags
      });
      setCurrentTeaserIndex(prev => prev + 1);
    } catch (error) {
      console.error('Error generating alternative teaser:', error);
    } finally {
      setIsGeneratingNewTeaser(false);
    }
  };

  const handleSwitchVersion = () => {
    const newIndex = currentTeaserIndex === 0 ? 1 : 0;
    setCurrentTeaserIndex(newIndex);
    setTeaser(teaserVersions[newIndex]);
  };

  const handleAnswer = (value) => {
    setAnswers(prev => ({
      ...prev,
      [QUESTIONS[currentQuestionIndex].id]: value
    }));
  };

  const handleBack = () => {
    if (currentStep === STEPS.TEASER) {
      setCurrentStep(STEPS.SUMMARY);
    } else if (currentStep === STEPS.QUESTIONS && currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    } else if (currentStep === STEPS.QUESTIONS && currentQuestionIndex === 0) {
      setCurrentStep(STEPS.INTRO);
    } else if (currentStep === STEPS.SUMMARY) {
      setCurrentStep(STEPS.QUESTIONS);
      setCurrentQuestionIndex(QUESTIONS.length - 1);
    }
  };

  const canProceed = () => {
    if (currentStep === STEPS.INTRO) return true;
    if (currentStep === STEPS.QUESTIONS) {
      const currentQuestion = QUESTIONS[currentQuestionIndex];
      return !currentQuestion.required || answers[currentQuestion.id];
    }
    return true;
  };

  // Add this new function to handle clicking on answers
  const handleAnswerClick = (questionIndex) => {
    setCurrentStep(STEPS.QUESTIONS);
    setCurrentQuestionIndex(questionIndex);
  };

  const handleVoiceSelect = (voiceId) => {
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setSelectedVoice(voiceId);
    setHasSelectedVoice(true);

    const voiceData = VOICE_DATA.find(v => v.id === voiceId);
    if (voiceData) {
      const newAudio = new Audio(voiceData.audioSrc);
      newAudio.play();
      setAudio(newAudio);
    }
  };

  // Add intersection observer for preview section
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsPreviewVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    const previewElement = document.getElementById('preview-section');
    if (previewElement) {
      observer.observe(previewElement);
    }

    return () => {
      if (previewElement) {
        observer.unobserve(previewElement);
      }
    };
  }, []);

  // Cleanup audio on component unmount
  useEffect(() => {
    return () => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    };
  }, [audio]);

  const scrollContainer = (direction) => {
    const container = document.getElementById('voices-container');
    const scrollAmount = direction === 'left' ? -200 : 200;
    container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  };

  const renderContent = () => {
    const direction = currentStep === STEPS.INTRO ? -1 : 1;

    return (
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={`${currentStep}-${currentQuestionIndex}`}
          custom={direction}
          variants={pageVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          className="w-full"
        >
          {currentStep === STEPS.INTRO && (
            <motion.div 
              variants={containerVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="flex flex-col items-center max-w-2xl mx-auto text-center min-h-[60vh] justify-center px-6"
            >
              <motion.p
                variants={contentVariants}
                className="text-[#7785FC] text-[1.125rem] md:text-[1.5rem] font-bold leading-normal mb-2 font-['Edu_TAS_Beginner']"
              > 
                JETZT NUR 3,90€!
              </motion.p>
              <motion.h2
                variants={contentVariants}
                className="text-[#343434] text-[3rem] md:text-[4.5rem] leading-[110%] tracking-[-0.105rem] md:tracking-[-0.135rem] font-freeman mb-4 md:mb-6"
              >
                Deine Geschichte wartet schon!
              </motion.h2>
              <motion.p
                variants={contentVariants}
                className="text-[#343434] text-base md:text-[1.25rem] font-normal leading-[150%] mb-1 md:mb-6 font-aleo max-w-[90%] md:max-w-none"
              >
                Nur ein paar kurze Fragen und wir erstellen deine magische Hörgeschichte. Die fertige Geschichte wird ungefähr 5 Minuten lang sein.
              </motion.p>
            </motion.div>
          )}

          {currentStep === STEPS.QUESTIONS && (
            <motion.div 
              variants={containerVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="flex flex-col items-center min-h-[calc(100vh-12rem)] text-center bg-[#EDD1BA] pt-[2vh] md:pt-[20vh]"
            >
              <motion.div className="w-full flex items-center justify-center">
                <motion.h2
                  key={`question-${currentQuestionIndex}`}
                  variants={contentVariants}
                  className="text-[#343434] text-[2.5rem] md:text-[4.5rem] leading-[110%] tracking-[-0.105rem] md:tracking-[-0.135rem] font-freeman max-w-full md:max-w-[95%]"
                >
                  {typeof QUESTIONS[currentQuestionIndex].question === 'function' 
                    ? QUESTIONS[currentQuestionIndex].question(answers)
                    : QUESTIONS[currentQuestionIndex].question}
                </motion.h2>
              </motion.div>
              
              <AnimatePresence mode="wait">
                <motion.div
                  key={`input-${currentQuestionIndex}`}
                  variants={containerVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  className="w-full mt-6 md:mt-12 px-4 md:px-6 max-w-3xl mx-auto"
                >
                  {QUESTIONS[currentQuestionIndex].types ? (
                    <div className="space-y-3 w-full">
                      <div className="space-y-3 w-full">
                        {QUESTIONS[currentQuestionIndex].options.map((option) => (
                          <button
                            key={option}
                            onClick={() => handleAnswer(option)}
                            className={`w-full px-6 py-5 rounded-[1.5rem] text-center transition-all duration-300 overflow-hidden ${
                              answers[QUESTIONS[currentQuestionIndex].id] === option
                                ? 'bg-[#FFA23E] text-[#343434]'
                                : 'bg-[#FFF3DD] hover:bg-[#FFE4B8]'
                            }`}
                          >
                            <span className="text-fit-multiline">{option}</span>
                          </button>
                        ))}
                      </div>
                      <div className="relative py-1">
                        <div className="text-center">
                          <span className="px-4 text-base text-[#343434]">oder</span>
                        </div>
                        <input
                          type="text"
                          value={answers[QUESTIONS[currentQuestionIndex].id] || ''}
                          onChange={(e) => handleAnswer(e.target.value)}
                          placeholder={QUESTIONS[currentQuestionIndex].placeholder}
                          className="w-full px-6 py-5 rounded-[1.5rem] bg-[#FFF3DD] outline-none ring-0 focus:ring-2 focus:ring-[#FFA23E] transition-shadow duration-300 text-center text-fit"
                        />
                      </div>
                    </div>
                  ) : QUESTIONS[currentQuestionIndex].type === 'select' ? (
                    <div className="space-y-3 w-full">
                      {QUESTIONS[currentQuestionIndex].options.map((option) => (
                        <button
                          key={option}
                          onClick={() => handleAnswer(option)}
                          className={`w-full px-6 py-5 rounded-[1.5rem] text-center transition-all duration-300 overflow-hidden ${
                            answers[QUESTIONS[currentQuestionIndex].id] === option
                              ? 'bg-[#FFA23E] text-[#343434]'
                              : 'bg-[#FFF3DD] hover:bg-[#FFE4B8]'
                          }`}
                        >
                          <span className="text-fit-multiline">{option}</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <input
                      type={QUESTIONS[currentQuestionIndex].type}
                      value={answers[QUESTIONS[currentQuestionIndex].id] || ''}
                      onChange={(e) => handleAnswer(e.target.value)}
                      placeholder={QUESTIONS[currentQuestionIndex].placeholder}
                      className="w-full px-6 py-5 rounded-[1.5rem] bg-[#FFF3DD] outline-none ring-0 focus:ring-2 focus:ring-[#FFA23E] transition-shadow duration-300 text-center text-fit"
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            </motion.div>
          )}

          {currentStep === STEPS.SUMMARY && (
            <motion.div
              variants={containerVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="w-screen relative left-[50%] right-[50%] mx-[-50vw] bg-[#EDD1BA] pt-4 md:pt-8 overflow-y-auto max-h-[calc(100vh-12rem)]"
            >
              <motion.div variants={contentVariants} className="max-w-full sm:max-w-[80%] md:max-w-[60%] xl:max-w-[50rem] mx-auto px-4 sm:px-6 xl:px-6 mb-0 pb-24">
                <motion.h2
                  variants={contentVariants}
                  className="text-[#7785FC] text-[1rem] md:text-[1.25rem] font-bold leading-normal text-left font-['Edu_TAS_Beginner'] mb-1"
                >
                  DEINE ANTWORTEN
                </motion.h2>
                <motion.h3 
                  variants={contentVariants}
                  className="text-[#343434] text-[1.75rem] md:text-[2.5rem] leading-[110%] tracking-[-0.06375rem] md:tracking-[-0.135rem] mb-2 md:mb-4 text-left font-freeman"
                >
                  Auf einen Blick
                </motion.h3>

                <motion.div 
                  variants={containerVariants}
                  className="space-y-1 md:space-y-8"
                >
                  {QUESTIONS.map((question, index) => (
                    <motion.div 
                      key={question.id}
                      variants={optionVariants}
                      custom={index}
                      className="flex flex-col"
                    >
                      <div className="max-w-[100%] md:max-w-[80%]">
                        <div className="inline-block py-1 md:py-2">
                          <p className="font-aleo text-[1rem] md:text-[1.125rem] text-[#343434] font-medium md:leading-[150%]">
                            {typeof question.question === 'function' 
                              ? question.question(answers)
                              : question.question}
                          </p>
                        </div>
                      </div>
                      <div className="flex md:justify-start">
                        <div className="max-w-[100%] md:max-w-[80%]">
                          <button
                            onClick={() => handleAnswerClick(index)}
                            className="inline-block bg-[#FFF3DD] rounded-[1.5rem] px-5 py-2 w-full text-left hover:bg-[#FFE4B8] transition-all duration-300 group relative"
                          >
                            <div className="flex items-center">
                              <div className="w-0 group-hover:w-[20px] transition-all duration-300 overflow-hidden flex items-center">
                                <SquarePen 
                                  size={16} 
                                  className="text-[#141414] flex-shrink-0 mr-2" 
                                />
                              </div>
                              <p className="font-aleo text-[1rem] md:text-[1.125rem] text-[#343434] font-medium group-hover:text-[#343434]">
                                {answers[question.id] || 'Keine Angabe'}
                              </p>
                            </div>
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </motion.div>
            </motion.div>
          )}

          {currentStep === STEPS.TEASER && (
            <motion.div
              variants={containerVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="w-screen relative left-[50%] right-[50%] mx-[-50vw] bg-[#EDD1BA] pt-4 md:pt-8"
            >
              <motion.div variants={contentVariants} className="max-w-full sm:max-w-[80%] md:max-w-[60%] xl:max-w-[50rem] mx-auto px-4 sm:px-6 xl:px-6 mb-0 pb-40">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  className="mb-4 md:mb-12"
                >
                  <motion.h4 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, ease: "easeOut", delay: 0.2 }}
                    className="text-[#343434] text-[1.5rem] leading-[110%] tracking-[-0.06375rem] mb-2 md:mb-4 md:text-center font-freeman"
                  >
                    Wähle deine Erzählstimme
                  </motion.h4>

                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, ease: "easeOut", delay: 0.4 }}
                    className="relative w-screen left-[50%] right-[50%] mx-[-50vw]"
                  >
                    <div 
                      id="voices-container"
                      className="flex overflow-x-auto scrollbar-hide snap-x snap-mandatory px-2 py-2 pb-1 md:pb-12 md:justify-center"
                      style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >
                      {VOICE_DATA.map((voice) => (
                        <div
                          key={voice.id}
                          className="flex-shrink-0 snap-center mr-2 sm:mr-1 md:mr-4"
                        >
                          <button
                            onClick={() => handleVoiceSelect(voice.id)}
                            className={`flex flex-col items-center py-4 px-2 md:px-4 rounded-2xl transition-all duration-300 ${
                              selectedVoice === voice.id ? 'bg-[#FFA23E]' : 'bg-[#FFF3DD]'
                            }`}
                            style={{ width: window.innerWidth >= 768 ? '9rem' : '6rem' }}
                          >
                            <div className="w-14 h-14 md:w-24 md:h-24 overflow-hidden flex-shrink-0 mb-2">
                              <img
                                src={voice.image}
                                alt={voice.name}
                                className="w-full h-full object-cover"
                                draggable="false"
                                onContextMenu={(e) => e.preventDefault()}
                                style={{ userSelect: 'none', pointerEvents: 'none' }}
                              />
                            </div>
                            <div className="flex items-center gap-1">
                              {selectedVoice === voice.id && audio && !audio.paused && (
                                <Play size={12} className="text-[#343434] fill-[#343434]" />
                              )}
                              <span className="font-freeman text-[#343434] text-center md:text-[1.25rem] font-normal leading-[110%] tracking-[-0.0375rem]">
                                {voice.name}
                              </span>
                            </div>
                          </button>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                </motion.div>

                {/* Magic text - appears after voice selection */}
                <motion.h3
                  initial={{ opacity: 1 }}
                  animate={{ opacity: 1 }}
                  className="text-[#343434] text-[1.5rem] leading-[110%] tracking-[-0.06375rem] mb-2 md:mb-4 md:text-center font-freeman overflow-hidden"
                >
                  {hasSelectedVoice && (
                    <span className="inline-flex">
                      <motion.span
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.4,
                          delay: 0,
                          ease: [0.2, 0.65, 0.3, 0.9]
                        }}
                      >
                        <WandSparkles className="inline w-6 h-6 -mt-1 mr-2" />
                      </motion.span>
                      {"Eine magische Geschichte wartet auf ".split("").map((char, i) => (
                        <motion.span
                          key={i}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{
                            duration: 0.4,
                            delay: 0.2 + (i * 0.03),
                            ease: [0.2, 0.65, 0.3, 0.9]
                          }}
                        >
                          {char === " " ? "\u00A0" : char}
                        </motion.span>
                      ))}
                      {(answers.name || "").split("").map((char, i) => (
                        <motion.span
                          key={i}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{
                            duration: 0.4,
                            delay: 1.2 + (i * 0.05),
                            ease: [0.2, 0.65, 0.3, 0.9]
                          }}
                        >
                          {char}
                        </motion.span>
                      ))}
                      <motion.span
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.4,
                          delay: 1.4 + (answers.name?.length || 0) * 0.05,
                          ease: [0.2, 0.65, 0.3, 0.9]
                        }}
                      >
                        …
                      </motion.span>
                    </span>
                  )}
                </motion.h3>

                {/* Refresh button - appears after teaser */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: showRefreshButton ? 1 : 0 }}
                  transition={{ duration: 0.4 }}
                  className="mt-2 relative z-30"
                >
                  <motion.button 
                    onClick={teaserVersions.length === 2 ? handleSwitchVersion : handleGenerateAlternativeTeaser}
                    disabled={isGeneratingNewTeaser}
                    className="h-8 px-2.5 mb-2 font-aleo text-[0.9rem] font-medium leading-[110%] tracking-[-0.02rem] flex flex-row items-center ml-auto transition-colors duration-500 text-[#343434] hover:bg-[#FFF3DD] rounded-xl"
                  >
                    {isGeneratingNewTeaser ? (
                      <RefreshCw className="mr-2 animate-spin" size={16} />
                    ) : teaserVersions.length === 2 ? (
                      <ArrowLeft className="mr-1 -mt-0.5" size={14} />
                    ) : (
                      <RefreshCw className="mr-1 -mt-0.5" size={14} />
                    )}
                    {teaserVersions.length === 2 ? 'Vorherige Version' : 'Neu Erstellen (1x)'}
                  </motion.button>
                </motion.div>

                {/* Teaser content - appears after magic text */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentTeaserIndex}
                    initial={{ 
                      opacity: 0,
                      filter: "blur(8px)",
                      y: -40
                    }}
                    animate={{ 
                      opacity: showTeaser ? 1 : 0.8,
                      filter: showTeaser ? "blur(0px)" : "blur(8px)",
                      y: 0
                    }}
                    exit={{ 
                      opacity: 0,
                      filter: "blur(8px)",
                      y: 40
                    }}
                    transition={{ 
                      duration: 0.8,
                      ease: "easeOut"
                    }}
                    className="relative"
                  >
                    <div 
                      className="bg-[#FFF3DD] rounded-t-[1.75rem] px-4 pb-4 pt-4 md:p-8"
                      style={{
                        maskImage: 'linear-gradient(to bottom, black 60%, transparent 100%)',
                        WebkitMaskImage: 'linear-gradient(to bottom, black 60%, transparent 100%)'
                      }}
                    >
                      <h3 className="font-freeman text-[2.25rem] md:text-4xl mb-4 md:mb-4 text-[#7785FC] leading-[110%] tracking-[-0.03rem]">
                        {teaser.title}
                      </h3>

                      <div className="flex flex-wrap gap-2 mb-3">
                        {teaser.tags.map((tag, index) => (
                          <span 
                            key={index}
                            className="bg-[#ffffff] text-[#343434] px-2.5 py-1.5 rounded-full font-['Aleo'] text-[0.75rem] font-medium leading-none tracking-[-0.006rem] flex items-center"
                          >
                            <span className="translate-y-[0.5px]">{tag}</span>
                          </span>
                        ))}
                      </div>
                      
                      <div>
                        <p className="font-['Aleo'] text-base md:text-[1.125rem] font-normal leading-[170%] md:leading-[180%] text-[#343434] pb-32 md:w-[98%]">
                          {teaser.teaser}
                        </p>
                      </div>
                    </div>

                    {/* Preview section with background image */}
                    <motion.div
                      id="preview-section"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ 
                        opacity: showTeaser ? 1 : 0,
                        scale: showTeaser ? 1 : 0.95
                      }}
                      transition={{ 
                        duration: 0.6,
                        delay: 2.2 // Add delay after teaser appears
                      }}
                      className="-mt-32 md:-mt-8 relative z-10 w-full py-24 pb-36"
                    >
                      {/* Background Image */}
                      <div 
                        className="absolute left-[50%] right-[50%] mx-[-50vw] w-screen h-[150%] md:h-[100%] top-1/2 -translate-y-1/2 md:max-w-[56rem] md:mx-auto md:left-1/2 md:-translate-x-1/2"
                        style={{
                          backgroundImage: 'url(https://i.imgur.com/ZNj20Aq.png)',
                          backgroundPosition: 'center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: `${window.innerWidth >= 768 ? 'contain' : '70%'}`
                        }}
                      />
                      
                      {/* Content */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="max-w-full sm:max-w-[90%] xl:max-w-[70rem] mx-auto px-4 sm:px-6 text-center">
                          <h2 className="text-[#343434] text-[1.5rem] leading-[110%] tracking-[-0.05rem] font-freeman mb-2">
                            Neugierig, was {answers.name} noch alles erlebt?
                          </h2>
                          <p className="font-aleo text-sm md:text-base text-[#343434] font-medium md:leading-[150%]">Jetzt die komplette Hörgeschichte freischalten!</p>
                        </div>
                      </div>
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ 
                        opacity: showTeaser ? 1 : 0,
                        y: showTeaser ? 0 : 20
                      }}
                      transition={{ 
                        duration: 0.4, 
                        delay: 4 // Add delay after preview section
                      }}
                      className="w-full max-w-2xl mx-auto px-4 pt-2 md:py-32 flex justify-center"
                    >
                      <div className="max-w-lg">
                        <h3 className="text-[#343434] text-[1.5rem] leading-[110%] tracking-[-0.05rem] font-freeman mb-2 text-center md:text-left md:mb-4">Das bekommst du alles:</h3>
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <div className="flex-shrink-0">
                              <CircleCheck className="text-[#7785FC] w-5 h-5" />
                            </div>
                            <span className="text-[#343434] text-sm md:text-base font-aleo">Die komplette Geschichte zum Nachlesen</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex-shrink-0">
                              <CircleCheck className="text-[#7785FC] w-5 h-5" />
                            </div>
                            <span className="text-[#343434] text-sm md:text-base font-aleo">Professionelle Sprachaufnahme</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex-shrink-0">
                              <CircleCheck className="text-[#7785FC] w-5 h-5" />
                            </div>
                            <span className="text-[#343434] text-sm md:text-base font-aleo">Unbegrenzt anhören, teilen, abspielen</span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                </AnimatePresence>
              </motion.div>
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>
    );
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#EDD1BA]" ref={formRef}>
      <div className="flex-1 w-full max-w-2xl mx-auto px-4 pt-24 pb-4">
        <div className="min-h-[400px] flex flex-col">
          {renderContent()}
        </div>
      </div>

      <div 
        ref={navigationRef}
        className="w-full pb-1 pt-2 fixed bottom-0 left-0 z-10 bg-[#EDD1BA]"
      >
        <div className="max-w-2xl mx-auto px-4 pb-2">
          {currentStep === STEPS.QUESTIONS && (
            <motion.div 
              className="w-full h-2 bg-[#FFF3DD] rounded-full mb-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <motion.div 
                className="h-full bg-[#FFA23E] rounded-full"
                initial={{ width: 0 }}
                animate={{ 
                  width: `${((currentQuestionIndex + 1) / QUESTIONS.length) * 100}%`
                }}
                transition={{ duration: 0.3 }}
              />
            </motion.div>
          )}
          <div className="flex items-center justify-between">
            {currentStep === STEPS.TEASER ? (
              <div className="w-full">
                <div className="w-full mb-1">
                  <p className="text-sm text-[#343434] text-center font-bold">
                    Gesamtbetrag: 3,90 €
                  </p>
                  <p className="text-sm text-[#343434] text-center flex items-center justify-center gap-1">
                    <Lock size={14} className="relative -top-[1px]" /> Jetzt sicher mit PayPal bezahlen.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleBack}
                    className="h-14 bg-transparent text-[#343434] px-4 rounded-full font-freeman text-[1.25rem] font-normal leading-[110%] tracking-[-0.0375rem] flex-shrink-0 hover:bg-[#FFF3DD] transition-colors duration-300"
                  >
                    Zurück
                  </button>
                  {PayPalButtonWrapper}
                </div>
              </div>
            ) : currentStep === STEPS.SUMMARY ? (
              <div className="w-full flex items-center gap-2">
                <button
                  onClick={handleBack}
                  className="h-14 bg-transparent text-[#343434] px-4 rounded-full font-freeman text-[1.25rem] font-normal leading-[110%] tracking-[-0.0375rem] flex-shrink-0 hover:bg-[#FFF3DD] transition-colors duration-300"
                >
                  Zurück
                </button>
                <button
                  onClick={handleNext}
                  disabled={isLoadingTeaser}
                  className="flex-grow h-14 bg-[#FFA23E] text-[#343434] px-8 rounded-full font-freeman text-[1.25rem] font-normal leading-[110%] tracking-[-0.0375rem] flex items-center justify-center hover:bg-[#FF9925] transition-colors duration-300"
                >
                  {isLoadingTeaser ? (
                    <div className="flex items-center gap-2">
                      <Loader size={20} className="animate-spin" style={{ animation: 'spin 2s linear infinite' }} />
                      <span>
                        {(() => {
                          const texts = [
                            "Sammle Ideen...",
                            "Verfasse Vorschau...", 
                            "Fast fertig..."
                          ];
                          const index = Math.floor((Date.now() % 5000) / (5000/3));
                          return texts[index];
                        })()}
                      </span>
                    </div>
                  ) : teaser ? (
                    'Zur Vorschau'
                  ) : (
                    'Vorschau erstellen'
                  )}
                </button>
              </div>
            ) : currentStep !== STEPS.INTRO ? (
              <button
                onClick={handleBack}
                className="h-14 bg-transparent text-[#343434] px-6 py-2 sm:py-4 rounded-full font-freeman text-[1.25rem] font-normal leading-[110%] tracking-[-0.0375rem] flex flex-col items-center justify-center hover:bg-[#FFF3DD] transition-colors duration-300"
              >
                Zurück
              </button>
            ) : null}
            
            {currentStep !== STEPS.TEASER && currentStep !== STEPS.SUMMARY && (
              currentStep === STEPS.INTRO ? (
                <motion.div
                  variants={contentVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  className="w-full"
                >
                  <motion.p className="text-base mb-4 text-center">
                    Noch Fragen? Schau mal <a href="/faq" className="underline hover:text-[#7785FC] relative">
                      <span className="inline-block hover:animate-[bounce_1s_infinite]">h</span>
                      <span className="inline-block hover:animate-[bounce_1s_infinite] delay-75">i</span>
                      <span className="inline-block hover:animate-[bounce_1s_infinite] delay-100">e</span>
                      <span className="inline-block hover:animate-[bounce_1s_infinite] delay-150">r</span>
                    </a>.
                  </motion.p>
                  <button
                    onClick={handleNext}
                    disabled={!canProceed()}
                    className="h-14 bg-[#FFA23E] text-[#343434] px-8 sm:px-6 py-2 sm:py-4 rounded-full font-freeman text-[1.25rem] font-normal leading-[110%] tracking-[-0.0375rem] flex flex-col items-center justify-center w-full hover:bg-[#FF9925] transition-colors duration-300"
                  >
                    Los gehts!
                  </button>
                </motion.div>
              ) : (
                <button
                  onClick={handleNext}
                  disabled={!canProceed()}
                  className={`h-14 px-8 sm:px-6 py-2 sm:py-4 rounded-full font-freeman text-[1.25rem] font-normal leading-[110%] tracking-[-0.0375rem] flex flex-col items-center justify-center transition-colors duration-300 ${
                    QUESTIONS[currentQuestionIndex].required && !answers[QUESTIONS[currentQuestionIndex].id]
                      ? 'bg-transparent border-2 border-[#FFA23E] text-[#343434] hover:bg-[#FFF3DD]'
                      : 'bg-[#FFA23E] text-[#343434] hover:bg-[#FF9925]'
                  }`}
                >
                  Weiter
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Generate;